import React from "react";
import {
  Button,
  Card,
  ColorPicker,
  Empty,
  Input,
  InputNumber,
  Select,
  Slider,
  Space,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import { Content } from "antd/es/layout/layout";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { PALETA_TEXTO, formatarVariacao, variacaoPercentual } from "./tema";

const { Text } = Typography;
const PRESETS = [{ label: "Cores da casa", colors: PALETA_TEXTO.map((c) => c.cor) }];

const OPCOES_SETA = [
  { value: "nenhuma", label: "Sem seta" },
  { value: "vertical", label: "Seta (sobe/desce conforme o sinal)" },
  { value: "cima", label: "Seta sempre para cima" },
  { value: "baixo", label: "Seta sempre para baixo" },
  { value: "lados", label: "Setas para os dois lados" },
];

/** Rótulo curto + controle, no mesmo alinhamento do painel de estilo. */
function Campo({ titulo, dica, children }) {
  const corpo = (
    <Content style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 30 }}>
      <Text style={{ fontSize: 12, width: 96, flexShrink: 0 }}>{titulo}</Text>
      {children}
    </Content>
  );
  return dica ? <Tooltip title={dica}>{corpo}</Tooltip> : corpo;
}

/** Índice negativo conta do fim, como no desenho. */
const indiceReal = (i, total) => (i < 0 ? total + i : i);

/** O mesmo número que vai aparecer na imagem — serve de conferência aqui. */
function previa(spec, periodo) {
  const valores = spec?.dados?.series?.[periodo.serie ?? 0]?.valores || [];
  if (valores.length < 2) return "—";
  const a = valores[indiceReal(periodo.de ?? 0, valores.length)];
  const b = valores[indiceReal(periodo.para ?? -1, valores.length)];
  const p = variacaoPercentual(a, b);
  return p === null ? "—" : formatarVariacao(p, periodo.decimais ?? 1);
}

const novoPeriodo = (categorias) => ({
  rotulo: "",
  serie: 0,
  de: 0,
  para: Math.max(0, (categorias?.length || 1) - 1),
  decimais: 1,
});

// A primeira nasce logo abaixo do cabeçalho (título + subtítulo + fonte
// ocupam cerca de um quarto da altura); as seguintes descem um degrau para
// não empilharem em cima da anterior.
const novaVariacao = (categorias, quantas = 0) => ({
  periodos: [novoPeriodo(categorias)],
  seta: "nenhuma",
  em: { gx: 0.5, gy: Math.min(0.85, 0.32 + quantas * 0.12) },
  tamanho: 11,
  escalaSeta: 1,
  corAutomatica: true,
  corTexto: "#FFFFFF",
  corFundo: "#000000",
  corBorda: "#FFFFFF",
});

/**
 * Painel de variações: caixas de percentual desenhadas sobre o gráfico.
 *
 * Vale para todas as famílias — a caixa é desenho, não série, então linha,
 * coluna+linha, empilhada e série longa passam a ter variação igual às
 * barras. Cada caixa reúne quantos períodos o usuário quiser ("1 ano" e
 * "5 anos" juntos, como nas referências) e cada período escolhe a série e
 * as duas pontas da comparação.
 */
export default function PainelVariacoes({ spec, onChange }) {
  const categorias = spec.dados?.categorias || [];
  const series = spec.dados?.series || [];
  const itens = spec.extras?.variacoes || [];

  const gravar = (lista) => onChange({ extras: { ...(spec.extras || {}), variacoes: lista } });
  const alterar = (i, mudanca) =>
    gravar(itens.map((item, j) => (i === j ? { ...item, ...mudanca } : item)));
  const alterarPeriodo = (i, k, mudanca) =>
    alterar(i, {
      periodos: itens[i].periodos.map((p, j) => (k === j ? { ...p, ...mudanca } : p)),
    });

  const opcoesCategoria = categorias.map((c, i) => ({ value: i, label: c }));
  const opcoesSerie = series.map((s, i) => ({ value: i, label: s.nome || `Série ${i + 1}` }));

  return (
    <Content style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Text type="secondary" style={{ fontSize: 11 }}>
        Caixas de variação desenhadas sobre o gráfico. Uma caixa pode reunir vários períodos — é assim que sai
        "1 ano: +29% • 5 anos: +2%" numa linha só.
      </Text>

      {!itens.length && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type="secondary" style={{ fontSize: 12 }}>Nenhuma variação no gráfico</Text>}
        />
      )}

      {itens.map((item, i) => (
        <Card
          key={i}
          size="small"
          title={<Text style={{ fontSize: 12 }}>Variação {i + 1}</Text>}
          styles={{ body: { padding: 10, display: "flex", flexDirection: "column", gap: 4 } }}
          extra={
            <Space size={4}>
              <Tooltip title="Mostrar no gráfico">
                <Switch
                  size="small"
                  checked={item.visivel !== false}
                  onChange={(v) => alterar(i, { visivel: v })}
                />
              </Tooltip>
              <Tooltip title="Remover">
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => gravar(itens.filter((_, j) => j !== i))}
                />
              </Tooltip>
            </Space>
          }
        >
          {(item.periodos || []).map((periodo, k) => (
            <Space key={k} size={4} wrap style={{ marginBottom: 4 }}>
              <Input
                size="small"
                style={{ width: 88 }}
                placeholder="rótulo"
                value={periodo.rotulo}
                onChange={(e) => alterarPeriodo(i, k, { rotulo: e.target.value })}
              />
              {opcoesSerie.length > 1 && (
                <Tooltip title="Série comparada">
                  <Select
                    size="small"
                    style={{ width: 96 }}
                    value={periodo.serie ?? 0}
                    options={opcoesSerie}
                    onChange={(v) => alterarPeriodo(i, k, { serie: v })}
                  />
                </Tooltip>
              )}
              <Tooltip title="De">
                <Select
                  size="small"
                  style={{ width: 82 }}
                  value={periodo.de ?? 0}
                  options={opcoesCategoria}
                  onChange={(v) => alterarPeriodo(i, k, { de: v })}
                />
              </Tooltip>
              <Tooltip title="Até">
                <Select
                  size="small"
                  style={{ width: 82 }}
                  value={periodo.para ?? -1}
                  options={opcoesCategoria}
                  onChange={(v) => alterarPeriodo(i, k, { para: v })}
                />
              </Tooltip>
              <Tooltip title="Casas decimais">
                <InputNumber
                  size="small"
                  style={{ width: 52 }}
                  min={0}
                  max={2}
                  value={periodo.decimais ?? 1}
                  onChange={(v) => alterarPeriodo(i, k, { decimais: v ?? 1 })}
                />
              </Tooltip>
              <Text strong style={{ fontSize: 12, width: 56, textAlign: "right" }}>
                {previa(spec, periodo)}
              </Text>
              {item.periodos.length > 1 && (
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    alterar(i, { periodos: item.periodos.filter((_, j) => j !== k) })
                  }
                />
              )}
            </Space>
          ))}

          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            style={{ alignSelf: "flex-start", marginBottom: 6 }}
            onClick={() =>
              alterar(i, { periodos: [...(item.periodos || []), novoPeriodo(categorias)] })
            }
          >
            Período
          </Button>

          <Campo titulo="Seta">
            <Select
              size="small"
              style={{ flex: 1 }}
              value={item.seta || "nenhuma"}
              options={OPCOES_SETA}
              onChange={(v) => alterar(i, { seta: v })}
            />
          </Campo>

          {item.seta && item.seta !== "nenhuma" && (
            <Campo titulo="Tam. da seta" dica="Multiplica o tamanho natural da seta.">
              <Slider
                style={{ flex: 1 }}
                min={0.4}
                max={3}
                step={0.1}
                value={item.escalaSeta ?? 1}
                onChange={(v) => alterar(i, { escalaSeta: v })}
              />
              <Tooltip title="Cor da seta (vazio segue o sinal da variação)">
                <ColorPicker
                  size="small"
                  allowClear
                  value={item.corSeta || null}
                  presets={PRESETS}
                  disabledAlpha
                  onChange={(c) => alterar(i, { corSeta: c.toHexString() })}
                  onClear={() => alterar(i, { corSeta: null })}
                />
              </Tooltip>
            </Campo>
          )}

          <Campo titulo="Posição X">
            <Slider
              style={{ flex: 1 }}
              min={0}
              max={100}
              value={Math.round((item.em?.gx ?? 0.5) * 100)}
              onChange={(v) => alterar(i, { em: { ...item.em, gx: v / 100 } })}
            />
          </Campo>
          <Campo titulo="Posição Y">
            <Slider
              style={{ flex: 1 }}
              min={0}
              max={100}
              value={Math.round((item.em?.gy ?? 0.32) * 100)}
              onChange={(v) => alterar(i, { em: { ...item.em, gy: v / 100 } })}
            />
          </Campo>

          <Campo titulo="Tamanho" dica="Tamanho da fonte da caixa.">
            <InputNumber
              size="small"
              style={{ width: 70 }}
              min={6}
              max={30}
              value={item.tamanho ?? 11}
              onChange={(v) => alterar(i, { tamanho: v ?? 11 })}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              cor pelo sinal
            </Text>
            <Switch
              size="small"
              checked={item.corAutomatica !== false}
              onChange={(v) => alterar(i, { corAutomatica: v })}
            />
          </Campo>

          <Campo titulo="Cores" dica="Texto, fundo e borda. Com a cor pelo sinal ligada, o fundo é verde na alta e vermelho na queda.">
            <Space size={4}>
              <Tooltip title="Texto">
                <ColorPicker
                  size="small"
                  value={item.corTexto || "#FFFFFF"}
                  presets={PRESETS}
                  disabledAlpha
                  onChange={(c) => alterar(i, { corTexto: c.toHexString() })}
                />
              </Tooltip>
              <Tooltip title="Fundo">
                <ColorPicker
                  size="small"
                  disabled={item.corAutomatica !== false}
                  value={item.corFundo || "#000000"}
                  presets={PRESETS}
                  disabledAlpha
                  onChange={(c) => alterar(i, { corFundo: c.toHexString() })}
                />
              </Tooltip>
              <Tooltip title="Borda">
                <ColorPicker
                  size="small"
                  value={item.corBorda || "#FFFFFF"}
                  presets={PRESETS}
                  disabledAlpha
                  onChange={(c) => alterar(i, { corBorda: c.toHexString() })}
                />
              </Tooltip>
            </Space>
          </Campo>
        </Card>
      ))}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => gravar([...itens, novaVariacao(categorias, itens.length)])}
      >
        Adicionar variação
      </Button>

      {spec.extras?.seta && (
        <>
          <Text strong style={{ fontSize: 12, marginTop: 4 }}>
            Seta principal (na margem)
          </Text>
          <Campo titulo="Tamanho" dica="Tamanho da seta grande que esta família desenha na margem esquerda.">
            <Slider
              style={{ flex: 1 }}
              min={0.4}
              max={2}
              step={0.1}
              value={spec.extras.seta.escala ?? 1}
              onChange={(v) =>
                onChange({
                  extras: { ...spec.extras, seta: { ...spec.extras.seta, escala: v } },
                })
              }
            />
          </Campo>
          <Campo titulo="Fonte do %">
            <InputNumber
              size="small"
              style={{ width: 70 }}
              min={6}
              max={30}
              value={spec.extras.seta.caixa?.tamanho ?? 11}
              onChange={(v) =>
                onChange({
                  extras: {
                    ...spec.extras,
                    seta: {
                      ...spec.extras.seta,
                      caixa: { ...(spec.extras.seta.caixa || {}), tamanho: v ?? 11 },
                    },
                  },
                })
              }
            />
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                const extras = { ...spec.extras };
                delete extras.seta;
                onChange({ extras });
              }}
            >
              Remover
            </Button>
          </Campo>
        </>
      )}
    </Content>
  );
}
